import { useUpdateAccountForCareseekerMutation } from "apollo/hooks/mutations";
import { TRACK_EVENTS } from "core/consts";
import {
  getApolloNetworkErrorDetails,
  getBackendNetworkErrorMessage,
} from "core/model/utils/errors";
import { Careseeker } from "core/types";
import { UserManagementUpdateAccountSchema } from "core/validationSchemas/tracking";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { AccountFormSubmittedValues } from "dsl/organisms/Accounts/useCreateAccountWithRoles";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useTracking } from "react-tracking";
import { useLocale, useTranslations } from "translations";
import Translations from "translations/types";
import { AccountsTableProps } from "./AccountsTable";
import { AccountDialogState, getTimestamp } from "./useUserManagementFeature";

export function getErrorMessage(
  err: unknown,
  defaultErrorMessage: string,
  translations: Translations,
) {
  const { statusCode } = getApolloNetworkErrorDetails(err) ?? {};
  const backendError = getBackendNetworkErrorMessage(err);
  let errorMessage = defaultErrorMessage;

  if (backendError?.includes("reached daily limit")) {
    errorMessage = translations.general.errorRequestLimit;
  } else if (
    statusCode === 403 &&
    backendError?.includes("associated with another company")
  ) {
    errorMessage = translations.userManagement.errorAccountOutsideGroup;
  }
  return errorMessage;
}

export function useUpdateAccountForCareseeker({
  careseeker,
  setAccountDialog,
}: {
  careseeker: Careseeker | undefined;
  setAccountDialog: Dispatch<SetStateAction<AccountDialogState>>;
}) {
  const locale = useLocale();
  const translations = useTranslations();
  const toast = useToast();
  const { trackEvent: trackUpdateEvent } =
    useTracking<UserManagementUpdateAccountSchema>();

  const [updateAccountMutation, updateProgress, reset] =
    useUpdateAccountForCareseekerMutation({ careseekerId: careseeker?.id });

  const updateAccount = useCallback(
    async (data: Parameters<AccountsTableProps["onUpdateAccount"]>[0]) => {
      try {
        await updateAccountMutation(data.account, {
          accountId: data.id,
          timestamp: getTimestamp(locale),
        });
        toast({ color: "success", message: data.successMessage });
        data.onSuccess?.();
      } catch (err) {
        toast({
          color: "danger",
          message: getErrorMessage(err, data.errorMessage, translations),
        });
        data.onError?.();
      } finally {
        reset();
      }
    },
    [toast, reset, updateAccountMutation],
  );

  const handleUpdateAccount = useCallback(
    async ({
      account_type: __, // BE doesn't allow passing 'account_type'
      dischargeLetterFileTypes: _dischargeLetterFileTypes,
      predictStations: _predictStations,
      send_activation_email: _, // BE doesn't allow passing 'send_activation_email'
      ...account
    }: AccountFormSubmittedValues) => {
      if (!account.id) {
        console.error("[handleUpdateAccount] account ID is required");
        throw new Error("Account ID is required");
      }

      const isReactivate = account.id && !!account.action?.type;

      try {
        // handler only accepts careseeker_roles
        delete account.roles.admin_roles;
        delete account.roles.careprovider_roles;

        trackUpdateEvent({
          edited_account_id: account.id,
          name: isReactivate
            ? TRACK_EVENTS.UM_REACTIVATE_MODAL_CONFIRMED
            : TRACK_EVENTS.UM_EDIT_MODAL_CONFIRMED,
        });

        const result = await updateAccountMutation(account, {
          accountId: account.id,
          timestamp: getTimestamp(locale),
        });

        if (!result?.data) {
          console.error("updateAccountMutation failed with result:", result);
          throw new Error("Account update failed");
        }
      } finally {
        reset();
        setAccountDialog({ step: null });
      }
    },
    [
      toast,
      reset,
      setAccountDialog,
      trackUpdateEvent,
      updateAccountMutation,
      careseeker?.id,
      translations,
    ],
  );

  return {
    handleUpdateAccount,
    trackUpdateEvent,
    updateAccount,
    updateProgress,
  };
}
